import React, { useEffect, useState } from 'react';
import {
    Card, CardHeader, CardContent, Divider, Stack, Button, IconButton,
    Table, TableHead, TableRow, TableCell, TableBody, Checkbox,
    Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
    Typography, TextField, Grid
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import axios from 'axios';
import moment from 'moment';
import { api } from "../../../../services/Main/Api";
import { useSnackbar } from "notistack";

import { getCondoId } from "../../../../services/Main/AuthStorage";
import { formatCpfCnpj, formatPhone, stripNonDigits } from "../../../../helpers/formatters";
import PaginationPage from "../../../../components/PaginationPage/PaginationPage";
import {useNavigate} from "react-router-dom";


const states = [
    'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
    'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'
];


export default function ContactListBase({ title, endpoint, defaultType }) {

    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [search, setSearch] = useState('');
    const [list, setList] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    const [errors, setErrors] = useState({});

    const [newContact, setNewContact] = useState({
        name: '',
        cpf_cnpj: '',
        email: '',
        phone: '',
        street: '',
        number: '',
        complement: '',
        district: '',
        city: '',
        state: '',
        cep: '',
        condo: getCondoId(),
        type: defaultType,
    });


    /** ===========================
     * LISTAGEM
     ============================*/

    const getList = () => {
        const filters = {
            search,
            page,
            page_size: 50,
            condo_id: getCondoId(),
            type: defaultType,
        };

        api.get(endpoint, { params: filters })
            .then(res => {
                setList(res.data);
                setSelectedIds([]);
            })
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        getList()
    }, [search, page, endpoint, defaultType]);


    /** ===========================
     * SELEÇÃO
     ============================*/
    const handleSelectAll = (e) => {
        const results = list?.results || [];
        setSelectedIds(e.target.checked ? results.map(i => i.id) : []);
    };

    const handleSelectOne = (id) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };


    /** ===========================
     * CRUD (criar / editar)
     ============================*/

    const validateFields = () => {
        const required = ['name', 'cpf_cnpj', 'street', 'number', 'cep', 'district', 'city', 'state'];

        const newErrors = {};
        required.forEach(field => {
            if (!newContact[field]?.trim()) newErrors[field] = 'Campo obrigatório';
        });

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleSave = () => {
        if (!validateFields()) return;

        const payload = {
            ...newContact,
            type: defaultType,
            cpf_cnpj: stripNonDigits(newContact.cpf_cnpj),
            phone: stripNonDigits(newContact.phone),
            cep: stripNonDigits(newContact.cep),
        };

        const request = editMode
            ? api.put(`${endpoint}${editId}/`, payload)
            : api.post(endpoint, payload);

        request
            .then(() => {
                enqueueSnackbar(editMode ? "Registro atualizado!" : "Registro criado!", { variant: "success" });
                setDialogOpen(false);
                setNewContact({
                    name: '',
                    cpf_cnpj: '',
                    email: '',
                    phone: '',
                    street: '',
                    number: '',
                    complement: '',
                    district: '',
                    city: '',
                    state: '',
                    cep: '',
                    condo: getCondoId(),
                    type: defaultType,
                });
                setErrors({});
                setEditMode(false);
                getList();
            })
            .catch(err => {
                console.error(err);
                enqueueSnackbar("Erro ao salvar", { variant: "error" });
            });
    };


    const handleRowEdit = (item) => {
        setEditMode(true);
        setEditId(item.id);
        setNewContact(item);
        setDialogOpen(true);
    };


    /** ===========================
     * DELETE
     ============================*/
    const handleDeleteConfirmed = () => {
        Promise.all(selectedIds.map(id => api.delete(`${endpoint}${id}/`)))
            .then(() => enqueueSnackbar("Itens removidos!", { variant: 'success' }))
            .catch(() => enqueueSnackbar("Erro ao remover!", { variant: 'error' }))
            .finally(() => {
                setConfirmOpen(false);
                setSelectedIds([]);
            });
    };


    /** ===========================
     * FORM: MUDANÇAS DE INPUT
     ============================*/
    const handleDialogChange = (e) => {
        const { name, value } = e.target;

        let formatted = value;

        if (name === "cpf_cnpj") formatted = formatCpfCnpj(value);
        if (name === "phone") formatted = formatPhone(value);

        setNewContact(prev => ({ ...prev, [name]: formatted }));
    };


    const handleCepBlur = async () => {
        const raw = newContact.cep.replace(/\D/g, '');
        if (raw.length !== 8) return;

        try {
            const res = await axios.get(`https://viacep.com.br/ws/${raw}/json/`);
            setNewContact(prev => ({
                ...prev,
                street: res.data.logradouro || '',
                district: res.data.bairro || '',
                city: res.data.localidade || '',
                state: res.data.uf || '',
            }));
        } catch (e) {
            console.error(e);
        }
    };


    /** ===========================
     * RENDER
     ============================*/

    const allSelected =
        list?.results?.length > 0 && selectedIds.length === list?.results?.length;

    const handleRowClick = (item) => {
        navigate(`/contato/${item.id}`);
    };

    return (
        <>
            <Card sx={{ m: 2 }}>
                <CardHeader title={`${title} (${list?.count ?? 0})`} />
                <Divider />

                <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 2, py: 1 }}>
                    <Button variant="contained" startIcon={<AddIcon />}
                            onClick={() => {
                                setEditMode(false);
                                setDialogOpen(true);
                            }}>
                        Criar {title.slice(0, -1)}
                    </Button>

                    <TextField
                        size="small"
                        placeholder="Pesquisar..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />
                        }}
                    />

                    <Button
                        variant="outlined"
                        size="small"
                        disabled={selectedIds.length === 0}
                        startIcon={<KeyboardArrowDownIcon />}
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                    >
                        Ações ({selectedIds.length})
                    </Button>

                    <Menu open={menuOpen} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
                        <MenuItem onClick={() => { setConfirmOpen(true); setAnchorEl(null); }}>
                            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                            Excluir selecionados
                        </MenuItem>
                    </Menu>
                </Stack>

                <CardContent>
                    {loading ? (
                        <Typography>Carregando...</Typography>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell padding="checkbox">
                                        <Checkbox checked={allSelected} onChange={handleSelectAll} />
                                    </TableCell>
                                    <TableCell>Nome</TableCell>
                                    <TableCell>CPF/CNPJ</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Telefone</TableCell>
                                    <TableCell>Criado em</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {list?.results?.map(item => (
                                    <TableRow key={item.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(item)}>
                                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={selectedIds.includes(item.id)}
                                                onChange={() => handleSelectOne(item.id)}
                                            />
                                        </TableCell>
                                        <TableCell>{item.name}</TableCell>
                                        <TableCell>{formatCpfCnpj(item.cpf_cnpj)}</TableCell>
                                        <TableCell>{item.email}</TableCell>
                                        <TableCell>{formatPhone(item.phone)}</TableCell>
                                        <TableCell>{moment(item.created_at).format("DD/MM/YYYY HH:mm")}</TableCell>
                                    </TableRow>
                                ))}

                                {!list?.results?.length && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">
                                            Nenhum registro encontrado
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>


            {/* PAGINAÇÃO */}
            <PaginationPage data={{ ...list, page }} onChange={setPage} />


            {/* DIALOG DE CONFIRMAÇÃO */}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>Confirmar exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir {selectedIds.length} itens?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
                    <Button onClick={handleDeleteConfirmed} color="error" variant="contained">Excluir</Button>
                </DialogActions>
            </Dialog>


            {/* FORMULÁRIO COMPLETO */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{editMode ? "Editar" : "Novo"} {title.slice(0, -1)}</DialogTitle>
                <Divider />

                <DialogContent>
                    <Grid container spacing={2}>
                        {['name', 'cpf_cnpj', 'email', 'phone'].map(field => (
                            <Grid item xs={12} sm={6} key={field}>
                                <TextField
                                    fullWidth
                                    name={field}
                                    size="small"
                                    label={
                                        field === 'name'
                                            ? 'Nome'
                                            : field === 'cpf_cnpj'
                                                ? 'CPF ou CNPJ'
                                                : field === 'email'
                                                    ? 'Email'
                                                    : 'Telefone'
                                    }
                                    value={newContact[field]}
                                    onChange={handleDialogChange}
                                    error={!!errors[field]}
                                    helperText={errors[field]}
                                />
                            </Grid>
                        ))}

                        {/* Endereço */}
                        <Grid item xs={12}>
                            <Typography sx={{ mt: 2 }} variant="h6">Endereço</Typography>
                        </Grid>

                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="CEP"
                                name="cep"
                                fullWidth
                                size="small"
                                value={newContact.cep}
                                onChange={handleDialogChange}
                                onBlur={handleCepBlur}
                                error={!!errors.cep}
                                helperText={errors.cep}
                            />
                        </Grid>

                        {['street', 'number', 'district', 'city', 'state', 'complement'].map(field => (
                            <Grid
                                item
                                xs={12}
                                sm={
                                    field === 'number' ? 3 :
                                        field === 'city' ? 4 :
                                            field === 'state' ? 2 : 6
                                }
                                key={field}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    select={field === 'state'}
                                    name={field}
                                    label={
                                        field === 'street' ? 'Rua' :
                                            field === 'number' ? 'Número' :
                                                field === 'district' ? 'Bairro' :
                                                    field === 'city' ? 'Cidade' :
                                                        field === 'state' ? 'UF' :
                                                            'Complemento'
                                    }
                                    value={newContact[field]}
                                    onChange={handleDialogChange}
                                    error={!!errors[field]}
                                    helperText={errors[field]}
                                >
                                    {field === 'state' &&
                                        states.map(uf => (
                                            <MenuItem key={uf} value={uf}>{uf}</MenuItem>
                                        ))
                                    }
                                </TextField>
                            </Grid>
                        ))}
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ justifyContent: "space-between", py: 2 }}>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained">Salvar</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
